import { useEffect, useMemo, useState } from 'react';

import type { RegistryOrderSubject } from '@/hooks/useRegistryOrderSubjects';
import type { BatchCompany } from './orderDraft';

interface UseOrderCompaniesOptions {
  opened: boolean;
  subjects: RegistryOrderSubject[];
}

/** One line of the companies checklist. */
export interface CompanyRow {
  subject: RegistryOrderSubject;
  ticked: boolean;
  country: string | null;
}

/**
 * The companies step when the order is for every company on the report:
 * which subjects are in, and the country each order goes to.
 *
 * Only the analyst's departures from the default are kept - the subjects
 * unticked and the countries changed - so a subject list that lands after
 * the step is on screen starts ticked, with its own first jurisdiction.
 */
export function useOrderCompanies({ opened, subjects }: UseOrderCompaniesOptions) {
  const [skipped, setSkipped] = useState<string[]>([]);
  const [countries, setCountries] = useState<Record<string, string | null>>({});

  // Cleared on close so reopening never inherits a previous selection.
  useEffect(() => {
    if (!opened) {
      setSkipped([]);
      setCountries({});
    }
  }, [opened]);

  const rows = useMemo<CompanyRow[]>(
    () =>
      subjects.map((subject) => ({
        subject,
        ticked: !skipped.includes(subject.id),
        country:
          subject.id in countries
            ? countries[subject.id]
            : (subject.jurisdictions[0]?.code ?? null),
      })),
    [subjects, skipped, countries]
  );

  const companies = useMemo<BatchCompany[]>(
    () =>
      rows
        .filter((row) => row.ticked && row.country !== null)
        .map((row) => ({ subject: row.subject, country: row.country as string })),
    [rows]
  );

  const ticked = rows.filter((row) => row.ticked);

  return {
    rows,
    setTicked: (id: string, isTicked: boolean) =>
      setSkipped((previous) =>
        isTicked ? previous.filter((entry) => entry !== id) : [...previous, id]
      ),
    setCountry: (id: string, country: string | null) =>
      setCountries((previous) => ({ ...previous, [id]: country })),
    /** The ticked companies that have a country, in checklist order. */
    companies,
    /** The distinct countries among them, in checklist order. */
    countries: useMemo(() => [...new Set(companies.map((c) => c.country))], [companies]),
    /** Something is ticked, and every ticked company has a country. */
    ready: ticked.length > 0 && ticked.every((row) => row.country !== null),
  };
}

export type OrderCompanies = ReturnType<typeof useOrderCompanies>;
